import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import PinDigitsInput from './PinDigitsInput';
import { PIN_LENGTH } from './pinFeatureFlag';

const linkStyles = {
  p: 0,
  border: 0,
  background: 'none',
  font: 'inherit',
  color: '#294190',
  fontWeight: 700,
  cursor: 'pointer',
  '&:hover': { color: '#294190', textDecoration: 'underline' },
};

const formatTime = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// "Enter your PIN" for a user whose persona signs in with a PIN. A lock in force replaces the
// boxes with when they can try again; "Forgot PIN?" is always there.
export default function PinEntryStep({
  phoneLabel,
  attemptsLeft,
  lockedUntil,
  otpFallback,
  onSubmit,
  onForgot,
  onUseOtp,
  onChangeNumber,
}) {
  const [pin, setPin] = useState('');
  const lockedAt = lockedUntil ? new Date(lockedUntil) : null;
  const [now, setNow] = useState(() => Date.now());
  const isLocked = lockedAt !== null && lockedAt.getTime() > now;

  // Re-check the lock every few seconds so the boxes come back by themselves when it ends.
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  // A wrong PIN clears the boxes for the next try.
  useEffect(() => {
    setPin('');
  }, [attemptsLeft]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pin.length === PIN_LENGTH && !isLocked) {
      onSubmit(pin);
    }
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={3} data-testid="login-pin-step">
      <Stack spacing={0.5} alignItems="center" textAlign="center">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Enter your PIN
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {phoneLabel}{' '}
          <Link component="button" type="button" onClick={onChangeNumber} underline="none"
            sx={{ ...linkStyles, fontWeight: 500 }} data-testid="login-pin-change-number">
            Change
          </Link>
        </Typography>
      </Stack>

      {isLocked ? (
        <Alert severity="warning" data-testid="login-pin-locked">
          Too many wrong PINs. Try again after {formatTime(lockedAt)}, or reset your PIN.
        </Alert>
      ) : (
        <>
          <PinDigitsInput value={pin} onChange={setPin} idPrefix="login-pin" label="PIN" autoFocus />
          {attemptsLeft ? (
            <Typography variant="caption" color="error.main" textAlign="center" data-testid="login-pin-attempts">
              Wrong PIN. {attemptsLeft === 1 ? '1 try' : `${attemptsLeft} tries`} left before your account is locked.
            </Typography>
          ) : null}
        </>
      )}

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={isLocked || pin.length !== PIN_LENGTH}
        data-testid="login-pin-submit"
      >
        Sign in
      </Button>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Link component="button" type="button" onClick={onForgot} underline="none" sx={linkStyles}
          data-testid="login-pin-forgot">
          Forgot PIN?
        </Link>
        {otpFallback ? (
          <Link component="button" type="button" onClick={onUseOtp} underline="none"
            sx={{ ...linkStyles, fontWeight: 500, color: 'text.secondary' }} data-testid="login-pin-use-otp">
            Sign in with a code instead
          </Link>
        ) : null}
      </Box>
    </Stack>
  );
}
